import React, { FC, useCallback, useMemo, useState } from "react";
import { LayoutRectangle, Platform, ScrollView, View } from "react-native";
import { useNavigate } from "react-router";
import { Box, Layout as AuroraLayout, Spinner } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { QueryStatus } from "@lookiero/messaging-react";
import { PayButton, Section } from "@lookiero/payments-front";
import { Country } from "@lookiero/sty-psp-locale";
import { Layout as UiLayout, Sticky, Text, useScreenSize } from "@lookiero/sty-psp-ui";
import { CheckoutItemStatus } from "../../../../domain/checkoutItem/model/checkoutItem";
import { useViewFirstAvailableCheckoutByCustomerId } from "../../../projection/checkout/react/useViewFirstAvailableCheckoutByCustomerId";
import { useViewPricingByCheckoutId } from "../../../projection/pricing/react/useViewPricingByCheckoutId";
import { TrackingPage } from "../../../tracking/tracking";
import { useTrackPageView } from "../../../tracking/useTrackPageView";
import { useTrackPressBack } from "../../../tracking/useTrackPressBack";
import { useTrackPressContinue } from "../../../tracking/useTrackPressContinue";
import { Body } from "../../components/layouts/body/Body";
import { CheckoutHeader } from "../../components/templates/header/checkoutHeader/CheckoutHeader";
import { useCheckoutFlow, OnSuccessFunction as OnCheckoutFlowSuccessFunction } from "../../hooks/useCheckoutFlow";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { ProductVariantThumbnail } from "../shared/components/productVariantThumbnail/ProductVariantThumbnail";
import { Pricing } from "../summary/components/pricing/Pricing";
import { style as checkoutStyle } from "./Checkout.style";
import { DeliveryBanner } from "./components/deliveryBanner/DeliveryBanner";
import { PaymentInstrument } from "./components/paymentInstrument/PaymentInstrument";

interface CheckoutProps {
  readonly layout: UiLayout;
  readonly getAuthToken: () => Promise<string>;
  readonly onCheckoutFlowSuccess: OnCheckoutFlowSuccessFunction;
  readonly useRedirect: () => Record<string, string>;
}

const Checkout: FC<CheckoutProps> = ({ layout: Layout, getAuthToken, useRedirect, onCheckoutFlowSuccess }) => {
  const {
    customer: { customerId, country, segment },
    basePath,
    tradename,
  } = useStaticInfo();
  const screenSize = useScreenSize();
  const isDektopScreen = screenSize === "L";

  const style = useMemo(() => checkoutStyle(), []);

  const titleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.CHECKOUT_TITLE });
  const [pricingHeight, setPricingHeight] = useState(0);
  const handleOnPricingLayout = useCallback(({ height }: LayoutRectangle) => setPricingHeight(height), []);

  const [checkout, checkoutStatus] = useViewFirstAvailableCheckoutByCustomerId({ customerId });
  const [pricing, pricingStatus] = useViewPricingByCheckoutId({ checkoutId: checkout?.id as string });

  const [checkoutFlow, checkoutFlowStatus, paymentFlowComponent] = useCheckoutFlow({
    checkout,
    getAuthToken,
    onSuccess: onCheckoutFlowSuccess,
  });

  useTrackPageView({
    page: TrackingPage.CHECKOUT,
    country,
    segment,
    tradename,
    checkoutId: checkout?.id,
  });

  const navigate = useNavigate();
  const trackPressContinue = useTrackPressContinue({
    page: TrackingPage.CHECKOUT,
    country,
    segment,
    tradename,
    checkoutId: checkout?.id,
  });
  const handleOnSubmit = useCallback(async () => {
    trackPressContinue();
    await checkoutFlow();
  }, [checkoutFlow, trackPressContinue]);

  const checkoutItemsKept = useMemo(
    () =>
      checkout?.items.filter(
        (checkoutItem) =>
          checkoutItem.status === CheckoutItemStatus.KEPT || checkoutItem.status === CheckoutItemStatus.REPLACED,
      ),
    [checkout?.items],
  );

  const hasReplacedCheckoutItem = useMemo(
    () => checkout?.items.some((checkoutItem) => checkoutItem.status === CheckoutItemStatus.REPLACED),
    [checkout?.items],
  );

  const trackPressBack = useTrackPressBack({
    page: TrackingPage.CHECKOUT,
    country,
    segment,
    tradename,
    checkoutId: checkout?.id,
  });
  const handleOnBack = useCallback(() => {
    trackPressBack();
    navigate(`${basePath}/${Routes.SUMMARY}`);
  }, [basePath, navigate, trackPressBack]);

  const PaymentButton = () =>
    useMemo(
      () => (
        <PayButton
          busy={checkoutFlowStatus === "loading"}
          section={Section.BOX_CHECKOUT}
          testID="confirm-checkout-button"
          small
          onPress={handleOnSubmit}
        />
      ),
      [],
    );

  const dependenciesLoadedStatuses = [QueryStatus.ERROR, QueryStatus.SUCCESS];
  const dependenciesLoaded =
    (dependenciesLoadedStatuses.includes(checkoutStatus) || checkout) &&
    dependenciesLoadedStatuses.includes(pricingStatus);

  if (!dependenciesLoaded) {
    return <Spinner />;
  }

  return (
    <Layout
      header={<CheckoutHeader onBack={handleOnBack} />}
      scrollEnabled={false}
      style={{
        header: style.header,
        scrollView: style.scrollView,
      }}
    >
      <ScrollView showsVerticalScrollIndicator={false} testID="checkout-view">
        {hasReplacedCheckoutItem && <DeliveryBanner />}

        <AuroraLayout
          fullWidth={!isDektopScreen}
          style={[isDektopScreen && style.desktopLayoutSpacing, !isDektopScreen && { paddingBottom: pricingHeight }]}
        >
          <Box size={{ L: "2/3" }} style={isDektopScreen && style.desktopListSpacing}>
            <View style={[style.contentWrapper, isDektopScreen && style.desktopContentWrapper]}>
              {country === Country.NL && (
                <View style={style.paymentSelectorNL}>
                  <PaymentInstrument boxUuid={checkout?.boxId} useRedirect={useRedirect} />
                </View>
              )}

              <Text level={3} style={style.title} variant="heading">
                {titleText}
              </Text>

              <View style={style.itemsContainer}>
                {checkoutItemsKept?.map((checkoutItem) => (
                  <View key={checkoutItem.id} testID="checkout-items-kept">
                    <ProductVariantThumbnail media={checkoutItem.productVariant.media} />
                  </View>
                ))}
              </View>

              {country !== Country.NL && (
                <View style={style.paymentSelector}>
                  <PaymentInstrument boxUuid={checkout?.boxId} useRedirect={useRedirect} />
                </View>
              )}
            </View>
          </Box>

          <Box size={{ L: "1/3" }} style={[style.resume, isDektopScreen && style.desktopResume]}>
            {pricing ? (
              <View style={[style.princingWrapper, !isDektopScreen && style.princingWrapperSmall]}>
                <Pricing pricing={pricing} totalCheckoutItemsKept={checkoutItemsKept?.length || 0} />

                {isDektopScreen && <PaymentButton />}
              </View>
            ) : null}
          </Box>
        </AuroraLayout>
      </ScrollView>

      {pricing && !isDektopScreen ? (
        <Sticky style={style.sticky} onLayout={Platform.OS !== "web" ? handleOnPricingLayout : undefined}>
          <Body>
            <PaymentButton />
          </Body>
        </Sticky>
      ) : null}

      {paymentFlowComponent}
    </Layout>
  );
};

export { Checkout };
